// Version 1 — Hour concentration heatmap (Phase × Level).
//
// Default view aggregates hours across all (Discipline × Channel) combos that
// have modeled FTE. The matrix has two row tiers:
//   - 5 OMCL phase rows (P1..P5) with bold pill labels.
//   - One indented sub-phase row beneath each phase (e.g. "Annual Strategic
//     Planning" beneath P1), one per `OMCL_SUB_PHASES` entry.
// Each phase row carries an expand/collapse chevron so the table can be
// flattened back to a phases-only view; an "Expand all"/"Collapse all"
// toggle in the header flips every phase at once. Columns are the job level
// groups (Junior..Executive); cell intensity scales with hours.
//
// Hours per cell are derived from:
//   FTE × FTE_CAPACITY × `model.timeConcentration[level, subPhase].share`
// where the FTE is the modeled grid with the scenario's manual cell adjustments
// applied, matching every other card on the tab,
// where each engine cell's (disciplineId, channelId, levelId) is mapped to
// the matching `SUB_DISCIPLINES` entry (parent disc id + channel-label
// match, with a "Programmatic + Digital Direct"-style multi-channel split).
// Sub-phases roll up to OMCL phases via the taxonomy in `maturity-taxonomy`.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import {
  FTE_CAPACITY,
  LEVEL_GROUPS,
  OMCL_PHASES,
  OMCL_SUB_PHASES,
  SUB_DISCIPLINES,
  levelGroupOf,
  type LevelGroupId,
} from '@rc/engine';
import {
  bgFor as bgForPalette,
  txtFor as txtForPalette,
  HEATMAP_PURPLE,
} from '../../lib/heatmap';
import { fmtHours } from '../../lib/format';
import type { DisciplineChannelCell, Model } from '@rc/engine/types';

interface Props {
  model: Model;
  /** The modeled grid with the scenario's manual adjustments applied. */
  cells: DisciplineChannelCell[];
}

interface FilterOption {
  /** disciplineId::channelId */
  id: string;
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  /** SUB_DISCIPLINES key this filter row maps onto for time-concentration lookups. */
  subDiscKey: string | null;
  fte: number;
}

interface HeatmapRow {
  phaseId: string;
  shortLabel: string;
  fullLabel: string;
  byLevel: Map<LevelGroupId, number>;
  total: number;
  /** Sub-phase rows belonging to this phase (taxonomy order). Each carries
   *  its own by-level breakdown and total so the row can render directly. */
  subRows: SubPhaseRow[];
}

interface SubPhaseRow {
  subPhaseId: string;
  label: string;
  byLevel: Map<LevelGroupId, number>;
  total: number;
}

// Slate→purple intensity gradient — shared with HoursHeatmap so the two
// cards read as the same visual language.
const PALETTE = HEATMAP_PURPLE;
const bgFor = (value: number, max: number) => bgForPalette(value, max, PALETTE);
const txtFor = (value: number, max: number) => txtForPalette(value, max, PALETTE);

const PHASE_PILL: Record<string, string> = {
  p1: 'bg-sky-100 text-sky-800 border-sky-200',
  p2: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  p3: 'bg-rose-100 text-rose-800 border-rose-200',
  p4: 'bg-amber-100 text-amber-800 border-amber-200',
  p5: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  // Non-campaign time reads as neutral overhead, not another campaign phase.
  p6: 'bg-slate-200 text-slate-700 border-slate-300',
};

function shortPhaseLabel(label: string): string {
  // OMCL phases are labelled "P1 — Business Planning" etc. Strip the "P# — ".
  const m = label.match(/^P\d+\s*[—-]\s*(.*)$/);
  return m ? m[1] : label;
}


function channelLabelFor(model: Model, channelId: string): string | null {
  if (channelId === 'all') return 'All';
  return (model.channels || []).find((c) => c.id === channelId)?.label ?? null;
}

function mapCellToSubDiscKey(model: Model, cell: DisciplineChannelCell): string | null {
  const candidates = SUB_DISCIPLINES.filter(
    (s) => s.parentDisciplineId === cell.disciplineId,
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0].key;

  const cellLabel = channelLabelFor(model, cell.channelId);
  if (!cellLabel) return candidates[0].key;
  const labelLc = cellLabel.toLowerCase();

  const exact = candidates.find((s) => s.channel.toLowerCase() === labelLc);
  if (exact) return exact.key;

  // Compound channels like "Programmatic + Digital Direct".
  const split = candidates.find((s) =>
    s.channel
      .split(/\s*\+\s*/)
      .some((part) => part.toLowerCase() === labelLc),
  );
  if (split) return split.key;

  // Fallback to "All" sub-disc, then first.
  const allLeg = candidates.find((s) => s.channel.toLowerCase() === 'all');
  if (allLeg) return allLeg.key;
  return candidates[0].key;
}

function buildFilterOptions(
  model: Model,
  cells: DisciplineChannelCell[],
): FilterOption[] {
  const map = new Map<string, FilterOption>();
  for (const c of cells || []) {
    const fte = Number(c.adjustedFte) || 0;
    if (fte <= 0) continue;
    const id = `${c.disciplineId}::${c.channelId}`;
    const existing = map.get(id);
    if (existing) {
      existing.fte += fte;
      continue;
    }
    map.set(id, {
      id,
      disciplineId: c.disciplineId,
      disciplineLabel: c.disciplineLabel,
      channelId: c.channelId,
      channelLabel:
        c.channelId === 'all' ? 'All channels' : c.channelLabel || c.channelId,
      subDiscKey: mapCellToSubDiscKey(model, c),
      fte,
    });
  }

  const discIndex = new Map(
    (model.disciplines || []).map((d, i) => [d.id, i] as const),
  );
  const chIndex = new Map(
    (model.channels || []).map((c, i) => [c.id, i] as const),
  );

  return Array.from(map.values()).sort((a, b) => {
    const da = discIndex.get(a.disciplineId) ?? Number.POSITIVE_INFINITY;
    const db = discIndex.get(b.disciplineId) ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    if (a.channelId === 'all' && b.channelId !== 'all') return -1;
    if (b.channelId === 'all' && a.channelId !== 'all') return 1;
    const ca = chIndex.get(a.channelId) ?? Number.POSITIVE_INFINITY;
    const cb = chIndex.get(b.channelId) ?? Number.POSITIVE_INFINITY;
    if (ca !== cb) return ca - cb;
    return a.channelLabel.localeCompare(b.channelLabel);
  });
}

interface HeatmapData {
  rows: HeatmapRow[];
  /** Phase × Level cells keyed `phaseId::levelId` → hours. */
  cellHours: Map<string, number>;
  colTotals: Partial<Record<LevelGroupId, number>>;
  total: number;
  /** Largest phase-row cell — used to scale the phase tier. */
  max: number;
  /** Largest sub-phase-row cell — used to scale the sub-phase tier so the
   *  finer-grained values aren't washed out by the phase rollups. */
  subMax: number;
  activeFte: number;
}

function buildHeatmap(
  model: Model,
  cells: DisciplineChannelCell[],
  selectedFilterIds: Set<string>,
): HeatmapData {
  const subPhaseToPhase = new Map(OMCL_SUB_PHASES.map((s) => [s.id, s.phaseId]));

  // Aggregate FTE by (subDiscKey, level) across the active filter selection.
  const fteByKeyLevel = new Map<string, number>();
  let activeFte = 0;
  for (const c of cells || []) {
    const fte = Number(c.adjustedFte) || 0;
    if (fte <= 0) continue;
    const filterId = `${c.disciplineId}::${c.channelId}`;
    if (selectedFilterIds.size > 0 && !selectedFilterIds.has(filterId)) continue;
    const subDiscKey = mapCellToSubDiscKey(model, c);
    if (!subDiscKey) continue;
    const k = `${subDiscKey}::${c.levelId}`;
    fteByKeyLevel.set(k, (fteByKeyLevel.get(k) || 0) + fte);
    activeFte += fte;
  }

  const cellHours = new Map<string, number>(); // phaseId::levelGroupId
  // Sub-phase × level-group cell hours, keyed `subPhaseId::levelGroupId`. Built in the
  // same sweep so we only iterate `model.timeConcentration` once.
  const subCellHours = new Map<string, number>();

  for (const row of model.timeConcentration || []) {
    const fte = fteByKeyLevel.get(`${row.subDisciplineKey}::${row.levelId}`);
    if (!fte) continue;
    const share = Number(row.share) || 0;
    if (share <= 0) continue;
    const hours = fte * FTE_CAPACITY * share;
    const phaseId = subPhaseToPhase.get(row.subPhaseId);
    if (!phaseId) continue;
    const groupId = levelGroupOf(row.levelId).id;
    const phaseKey = `${phaseId}::${groupId}`;
    cellHours.set(phaseKey, (cellHours.get(phaseKey) || 0) + hours);

    const subKey = `${row.subPhaseId}::${groupId}`;
    subCellHours.set(subKey, (subCellHours.get(subKey) || 0) + hours);
  }

  const levels = LEVEL_GROUPS.map((g) => g.id);

  // Build sub-rows first, grouped by phase, so each phase row can carry its
  // children in taxonomy order.
  const subRowsByPhase = new Map<string, SubPhaseRow[]>();
  for (const sp of OMCL_SUB_PHASES) {
    const byLevel = new Map<LevelGroupId, number>();
    let total = 0;
    for (const lv of levels) {
      const v = subCellHours.get(`${sp.id}::${lv}`) || 0;
      if (v > 0) byLevel.set(lv, v);
      total += v;
    }
    const list = subRowsByPhase.get(sp.phaseId) || [];
    list.push({ subPhaseId: sp.id, label: sp.label, byLevel, total });
    subRowsByPhase.set(sp.phaseId, list);
  }

  const rows: HeatmapRow[] = OMCL_PHASES.map((p) => {
    const byLevel = new Map<LevelGroupId, number>();
    let total = 0;
    for (const lv of levels) {
      const v = cellHours.get(`${p.id}::${lv}`) || 0;
      if (v > 0) byLevel.set(lv, v);
      total += v;
    }
    return {
      phaseId: p.id,
      shortLabel: shortPhaseLabel(p.label),
      fullLabel: p.label,
      byLevel,
      total,
      subRows: subRowsByPhase.get(p.id) || [],
    };
  });

  const colTotals: Partial<Record<LevelGroupId, number>> = {};
  let max = 0;
  let total = 0;
  for (const lv of levels) {
    let s = 0;
    for (const r of rows) s += r.byLevel.get(lv) || 0;
    colTotals[lv] = s;
    total += s;
  }
  for (const v of cellHours.values()) if (v > max) max = v;
  let subMax = 0;
  for (const v of subCellHours.values()) if (v > subMax) subMax = v;

  return {
    rows,
    cellHours,
    colTotals,
    total,
    max,
    subMax,
    activeFte,
  };
}

export default function PhaseConcentrationCard({ model, cells }: Props) {
  const filterOptions = useMemo(
    () => buildFilterOptions(model, cells),
    [model, cells],
  );

  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  // Phases default to expanded so the user immediately sees sub-phase detail
  // (the original ask). Each phase id present in this set is rendered with
  // its sub-rows; toggling the chevron adds/removes from the set.
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(
    () => new Set(OMCL_PHASES.map((p) => p.id)),
  );

  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pickerOpen]);

  const heatmap = useMemo(
    () => buildHeatmap(model, cells, selected ?? new Set()),
    [model, cells, selected],
  );

  // Hide group columns where no phase has any hours, so we don't render a dead
  // Executive column for scenarios that don't staff senior levels.
  const activeGroups = useMemo(
    () => LEVEL_GROUPS.filter((g) => (heatmap.colTotals[g.id] || 0) > 0),
    [heatmap.colTotals],
  );

  const isAll = selected == null || selected.size === 0;
  const selectedCount = selected ? selected.size : 0;

  const toggleOption = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur ?? new Set<string>());
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next.size === 0 ? null : next;
    });
  };
  const clearSelection = () => setSelected(null);
  const selectAll = () => setSelected(new Set(filterOptions.map((o) => o.id)));

  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };
  const expandAll = () =>
    setExpandedPhases(new Set(OMCL_PHASES.map((p) => p.id)));
  const collapseAll = () => setExpandedPhases(new Set());
  const allExpanded = expandedPhases.size === OMCL_PHASES.length;

  const subtitleSummary = isAll
    ? `Aggregated across ${filterOptions.length} (Discipline × Channel) combos and all levels.`
    : `${selectedCount} of ${filterOptions.length} (Discipline × Channel) combo${
        selectedCount === 1 ? '' : 's'
      } selected.`;

  return (
    <section className="card">
      <div className="card-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-brand-purple-500" />
              <h3 className="text-base font-semibold">
                Hour concentration — Phase × Level
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Annual modeled hours distributed across the 5 OMCL workflow phases
              (P1–P5) and job levels. Hours = FTE ×{' '}
              <span className="font-medium text-slate-600">
                {FTE_CAPACITY.toLocaleString()} hrs/yr
              </span>{' '}
              × Time concentration share. {subtitleSummary}
            </p>
          </div>
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className={clsx(
                'btn-secondary text-xs whitespace-nowrap',
                !isAll && 'border-brand-purple-300 bg-brand-purple-50 text-brand-purple-800',
              )}
            >
              <Filter size={13} />
              {isAll ? 'All disciplines & channels' : `${selectedCount} selected`}
              <ChevronDown size={12} className="opacity-60" />
            </button>
            {pickerOpen && (
              <div className="absolute left-0 mt-1 w-[320px] max-w-[calc(100vw-2rem)] z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[440px] overflow-hidden flex flex-col">
                <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Filter by Discipline × Channel
                  </div>
                  <div className="flex items-center gap-1 text-[11px]">
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="text-slate-500 hover:text-slate-800"
                    >
                      All
                    </button>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-slate-500 hover:text-slate-800"
                    >
                      Pick all
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto py-1">
                  {filterOptions.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-slate-400">
                      No staffed (Discipline × Channel) combos in this scenario.
                    </div>
                  ) : (
                    filterOptions.map((o) => {
                      const on = !isAll && selected!.has(o.id);
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => toggleOption(o.id)}
                          className={clsx(
                            'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-slate-50',
                            on && 'bg-brand-purple-50/60',
                          )}
                        >
                          <span className="text-left flex-1 min-w-0">
                            <div className="font-medium text-slate-700 truncate">
                              {o.disciplineLabel}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {o.channelLabel}
                              {!o.subDiscKey && (
                                <span className="ml-1 text-amber-600">· no time data</span>
                              )}
                            </div>
                          </span>
                          <span className="tabular-nums text-[11px] text-slate-500">
                            {o.fte.toFixed(1)} FTE
                          </span>
                          {on && <Check size={12} className="text-brand-purple-600 shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="border-t border-slate-100 px-3 py-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(false)}
                    className="text-xs text-brand-purple-600 hover:text-brand-purple-800"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {!isAll && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {filterOptions
              .filter((o) => selected!.has(o.id))
              .map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggleOption(o.id)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-purple-50 text-brand-purple-800 border border-brand-purple-200 text-[11px]"
                  title="Click to remove"
                >
                  {o.disciplineLabel} · {o.channelLabel}
                  <X size={10} className="opacity-70" />
                </button>
              ))}
          </div>
        )}
      </div>

      <div className="card-body space-y-4">
        {heatmap.total <= 0 ? (
          <div className="text-sm text-slate-500">
            No hours to chart for the current selection.{' '}
            {!isAll && (
              <button
                type="button"
                onClick={clearSelection}
                className="text-brand-purple-600 hover:text-brand-purple-800 underline"
              >
                Clear filter
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-end -mt-1">
              <button
                type="button"
                onClick={allExpanded ? collapseAll : expandAll}
                className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                title={allExpanded ? 'Hide every sub-phase row' : 'Show every sub-phase row'}
              >
                {allExpanded ? (
                  <>
                    <ChevronDown size={11} />
                    Collapse sub-phases
                  </>
                ) : (
                  <>
                    <ChevronRight size={11} />
                    Expand sub-phases
                  </>
                )}
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
                    <th className="text-left py-2 pr-3 font-medium">Phase / sub-phase</th>
                    {activeGroups.map((g) => (
                      <th
                        key={g.id}
                        className="text-center py-2 px-1.5 font-medium whitespace-nowrap normal-case tracking-normal"
                      >
                        {g.label}
                      </th>
                    ))}
                    <th className="text-right py-2 pl-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {heatmap.rows.map((row) => {
                    const expanded = expandedPhases.has(row.phaseId);
                    const hasSubs = row.subRows.length > 0;
                    return (
                      <Fragment key={row.phaseId}>
                        <tr className="border-t border-slate-100">
                          <td className="py-1.5 pr-3 align-middle whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => togglePhase(row.phaseId)}
                                disabled={!hasSubs}
                                className={clsx(
                                  'text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors',
                                )}
                                title={
                                  !hasSubs
                                    ? 'No sub-phases'
                                    : expanded
                                      ? 'Hide sub-phases'
                                      : 'Show sub-phases'
                                }
                              >
                                {expanded ? (
                                  <ChevronDown size={13} />
                                ) : (
                                  <ChevronRight size={13} />
                                )}
                              </button>
                              <span
                                className={clsx(
                                  'pill text-[10px] uppercase tracking-wider border',
                                  PHASE_PILL[row.phaseId] ?? 'bg-slate-100 text-slate-700 border-slate-200',
                                )}
                                title={row.fullLabel}
                              >
                                {row.phaseId.toUpperCase()}
                              </span>
                              <span className="text-slate-700 font-medium">
                                {row.shortLabel}
                              </span>
                            </div>
                          </td>
                          {activeGroups.map((g) => {
                            const v = row.byLevel.get(g.id) || 0;
                            return (
                              <td key={g.id} className="px-0.5 py-0.5">
                                <div
                                  className="w-full rounded-md py-1.5 px-1.5 text-center text-xs font-medium"
                                  style={{
                                    backgroundColor: bgFor(v, heatmap.max),
                                    color: txtFor(v, heatmap.max),
                                  }}
                                  title={`${row.fullLabel} × ${g.label}: ${Math.round(v).toLocaleString()} hours`}
                                >
                                  {fmtHours(v)}
                                </div>
                              </td>
                            );
                          })}
                          <td className="py-1.5 pl-3 text-right font-semibold text-slate-700">
                            {fmtHours(row.total)}
                          </td>
                        </tr>
                        {expanded &&
                          row.subRows.map((sub) => (
                            <tr
                              key={sub.subPhaseId}
                              className="bg-slate-50/40"
                            >
                              <td className="py-1 pr-3 align-middle">
                                <div className="flex items-center gap-2 pl-7">
                                  <span className="text-[10px] text-slate-400 tabular-nums">
                                    └
                                  </span>
                                  <span className="text-[11px] text-slate-600">
                                    {sub.label}
                                  </span>
                                </div>
                              </td>
                              {activeGroups.map((g) => {
                                const v = sub.byLevel.get(g.id) || 0;
                                return (
                                  <td key={g.id} className="px-0.5 py-0.5">
                                    <div
                                      className="w-full rounded-md py-1 px-1.5 text-center text-[11px]"
                                      style={{
                                        backgroundColor: bgFor(v, heatmap.subMax),
                                        color: txtFor(v, heatmap.subMax),
                                      }}
                                      title={`${sub.label} × ${g.label}: ${Math.round(v).toLocaleString()} hours`}
                                    >
                                      {fmtHours(v)}
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="py-1 pl-3 text-right text-[11px] font-medium text-slate-600">
                                {fmtHours(sub.total)}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                  <tr className="border-t-2 border-slate-300 bg-slate-50/40">
                    <td className="py-2 pr-3 font-semibold text-slate-700">Total</td>
                    {activeGroups.map((g) => (
                      <td
                        key={g.id}
                        className="py-2 px-1.5 text-center font-semibold text-slate-700"
                      >
                        {fmtHours(heatmap.colTotals[g.id] || 0)}
                      </td>
                    ))}
                    <td className="py-2 pl-3 text-right font-bold text-brand-purple-700">
                      {fmtHours(heatmap.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 text-[11px] text-slate-500">
              <span>
                Total{' '}
                <span className="text-slate-700 font-medium tabular-nums">
                  {fmtHours(heatmap.total)}
                </span>{' '}
                hrs · {' '}
                <span className="tabular-nums">{heatmap.activeFte.toFixed(1)}</span> FTE in scope ·
                avg{' '}
                <span className="tabular-nums">
                  {(heatmap.total / Math.max(0.0001, heatmap.activeFte)).toFixed(0)}
                </span>{' '}
                hrs/FTE
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgb(248,250,252)', border: '1px solid #e2e8f0' }} />
                Less
                <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgb(123,91,182)' }} />
                More hours
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
